<script setup>
import PageWrapper from '@/components/shared/page/PageWrapper.vue'
import ChatInterface from '@/components/chat/ChatInterface.vue'
import TypingIndicator from '@/components/chat/TypingIndicator.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import {myTeammateApi} from '@/api/my-teammate.api.js'
import {usePage} from '@/composables/use-page.js'
import {useLoading} from '@/composables/use-loading.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useTeammateMessagesQuerying} from '@/composables/use-teammate-messages-querying.js'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {teammateConversationStates} from '@/constants/teammate.js'

//Setup page. "Arist Agent" is the employee-facing name for this feature (the
//admin-facing name is "Employee Agent") — "teammate" only lives in code, never
//in anything a user sees.
const {setTitle} = usePage()
setTitle('Arist Agent')

//Composables
const {processError} = useErrorHandling()
const {isLoading, setLoading} = useLoading(true)
const {goToHome} = useNavigation()
const {isAgentSurfaceEnabled} = useCurrentOrganization()

//Teammate messages querying (conversation comes from the same endpoint)
const {
  conversation,
  messages,
  loadMessages,
  updateMessages,
  startPollingMessages,
  stopPollingMessages,
} = useTeammateMessagesQuerying()

//Typing indicator. justSent is optimistic — it covers the brief gap before the
//agent run is observable; the backend `assistant_typing` flag (driven off the
//agent-run lifecycle) covers proactive reach-outs and the real generation
//window. We clear the optimistic flag once the teammate's reply lands, with a
//timeout as a safety net (e.g. the agent skips on a guardrail).
const justSent = ref(false)
let justSentTimer = null
const flagJustSent = () => {
  justSent.value = true
  clearTimeout(justSentTimer)
  justSentTimer = setTimeout(() => (justSent.value = false), 30000)
}
const backendTyping = computed(() =>
  Boolean(conversation.value?.assistantTyping)
)
const isTyping = computed(() => justSent.value || backendTyping.value)

//Clear the optimistic flag as soon as an outgoing (teammate) message arrives.
watch(messages, () => {
  if (messages.value[messages.value.length - 1]?.isOutgoing) {
    justSent.value = false
    clearTimeout(justSentTimer)
  }
})

//User message handler
const processUserMessage = async message => {

  //Pause polling for the duration of the send. The optimistic message has no id
  //yet, and the API doesn't echo a correlation id, so a poll that lands mid-send
  //can't dedupe it — it would append the server copy as a duplicate bubble. With
  //the interval paused, no concurrent load runs during the unconfirmed window;
  //we resume once the persisted id is stamped on. (The eventual robust fix is
  //external_id correlation once the API persists it — see the composable.)
  stopPollingMessages()

  try {
    const persisted = await myTeammateApi.createMessage(message.body)

    //Stamp the persisted id and timestamp onto the optimistic message so a
    //subsequent poll dedupes it by id (the API doesn't echo a correlation id).
    message.id = persisted.id
    message.sentAt = persisted.sentAt
    updateMessages()

    //Show "typing…" right away and reset the polling timer. The assistant reply
    //arrives asynchronously (short agent debounce) and surfaces on the next poll.
    flagJustSent()
    startPollingMessages()
  }
  catch (error) {
    message.error = error
    updateMessages()
    processError(error)

    //Resume polling even on a failed send so proactive reach-outs still arrive.
    startPollingMessages()
  }
}

//Whether the thread has any messages yet
const hasMessages = computed(() => messages.value.length > 0)

//The composer stays hidden until the agent has sent the first message: by
//design, the feature is essentially invisible to the employee until the agent
//reaches out — the user never initiates a brand-new thread, regardless of the
//conversation state.
const isChatDisabled = computed(() => !hasMessages.value)

//Empty-state copy depends on the conversation state
const emptyStateMessage = computed(() => {
  if (conversation.value?.state === teammateConversationStates.UNRESPONSIVE) {
    return `You're all caught up — your Arist Agent will reach out when there's something new.`
  }
  return `Your Arist Agent will reach out when there's something for you.`
})

//On mount hook
onMounted(async () => {

  //The agent surface only exists when the org routes agent delivery to the
  //tab — send a stray visit (stale deep link, bookmark) home instead of
  //polling an endpoint that will refuse it
  if (!isAgentSurfaceEnabled.value) {
    return goToHome()
  }

  //Load the teammate thread. Only start polling once the initial load succeeds —
  //otherwise we'd kick off a 2s interval against a failing endpoint. Clear the
  //loading spinner in finally so it never sticks if the load rejects.
  try {
    await loadMessages()

    //Start polling messages
    startPollingMessages()
  }
  catch (error) {
    processError(error)
    goToHome()
  }
  finally {
    setLoading(false)
  }
})

//On unmount hook
onUnmounted(() => {
  stopPollingMessages()
  clearTimeout(justSentTimer)
})
</script>

<template>
  <PageWrapper
    is-fixed-height
    can-go-back
    @back="goToHome()"
  >
    <template #header>
      <h1 :class="$style.displayName">
        Arist Agent
      </h1>
    </template>

    <Spinner
      v-if="isLoading"
      is-block
    />

    <template v-else>
      <ChatInterface
        :messages="messages"
        :is-chat-disabled="isChatDisabled"
        show-timestamps
        @message="processUserMessage($event.message)"
      >
        <template #after-messages>
          <TypingIndicator v-if="isTyping" />
          <small
            v-if="!hasMessages && !isTyping"
            text-muted
            :class="$style.emptyState"
          >{{ emptyStateMessage }}</small>
        </template>
      </ChatInterface>
    </template>
  </PageWrapper>
</template>

<style module>
.displayName {
  font-size: var(--font-size-heading-xs);
  font-weight: var(--font-weight-bold);
  @media (min-width: 768px) {
    font-size: var(--font-size-heading-s);
  }
}
.emptyState {
  display: block;
  margin: 2rem auto;
  text-align: center;
}
</style>
